import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBoolean } from 'usehooks-ts';
import { useTranslation } from 'react-i18next';
import { CreateWorkspaceDialog } from '@/modules/workspaces';

import {
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  SidebarMenuButton,
  SidebarMenuItem,
  Skeleton,
  Text,
  useSidebar,
} from '@kognitos/lattice';
import { useCurrentUserOrganizationWorkspaces } from '@/generated/hooks/agora/v1/workspace';
import {
  useUserPreferencesStore,
  selectCurrentWorkspaceId,
  selectCurrentOrganizationId,
} from '@/shared/stores';
import WorkspaceIdentifier from '@/shared/components/workspace/WorkspaceIdentifier';
import { getRoutePath } from '@/routes/util';
import type {
  WorkspaceColorType,
  WorkspaceIconType,
} from '@/modules/onboarding/constants';
import { HotkeyTrigger } from '@/shared/components/HotkeyTrigger';
import { parseResourceName } from '@/shared/utils';

import { isWorkspaceItem, isOrganizationItem } from './util';

const WorkspaceSwitcher = ({
  isCollapsed,
  onDropdownOpenChange,
}: {
  isCollapsed: boolean;
  onDropdownOpenChange?: (open: boolean) => void;
}) => {
  const { t: tNav } = useTranslation('navigation');
  const { state: sidebarState } = useSidebar();
  const {
    value: isOpen,
    setTrue: setMenuOpen,
    setFalse: setMenuClose,
  } = useBoolean(false);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    value: isCreateWorkspaceDialogOpen,
    setTrue: openCreateWorkspaceDialog,
    setFalse: closeCreateWorkspaceDialog,
  } = useBoolean();
  const isWorkspaceSelected = isWorkspaceItem(location.pathname);
  const isOrgSelected = isOrganizationItem(location.pathname);

  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );

  const {
    data: workspacesData,
    isLoading: isWorkspacesLoading,
    isError: isWorkspacesError,
  } = useCurrentUserOrganizationWorkspaces(
    currentOrganizationId,
    { page_size: 20 },
    {
      query: { retry: false },
    },
  );

  // Compute the current workspace and auto-select first one if needed
  const currentWorkspace = useMemo(() => {
    const organizationWorkspaces = workspacesData?.data.user_workspaces;
    if (!organizationWorkspaces?.length) return null;

    return (
      organizationWorkspaces.find(workspace => {
        if (!workspace.name) return false;
        const workspaceId = parseResourceName(workspace.name).workspace;
        return workspaceId === currentWorkspaceId;
      }) || organizationWorkspaces[0]
    );
  }, [workspacesData, currentWorkspaceId]);

  // Only show selected state if workspace is selected and organization is not selected
  const shouldShowSelected = isWorkspaceSelected && !isOrgSelected;

  const handleWorkspaceSelect = (newWorkspaceId: string) => {
    if (!currentOrganizationId || !newWorkspaceId) return;
    navigate(
      getRoutePath('AUTOMATIONS', {
        orgId: currentOrganizationId,
        workspaceId: newWorkspaceId,
      }),
    );
  };

  // Map workspace data to display format with shortcuts
  const workspacesWithShortcuts = useMemo(() => {
    return (
      workspacesData?.data.user_workspaces?.map((workspace, index) => ({
        ...workspace,
        shortcut: `mod+alt+${index + 1}`,
        // Use default values for icon and color
        displayIcon: workspace.workspace_icon as WorkspaceIconType,
        displayColor: workspace.workspace_icon_color as WorkspaceColorType,
      })) || []
    );
  }, [workspacesData]);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setMenuOpen();
    } else {
      setMenuClose();
    }
    onDropdownOpenChange?.(open);
  };

  // Handle error state
  if (isWorkspacesError) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          size="default"
          variant="outline"
          className="h-8 !p-1.5 gap-0 group-data-[collapsible=icon]:!p-1.5 !transition-none text-destructive hover:text-destructive"
          disabled
        >
          <Icon type="AlertCircle" size="sm" className="text-destructive" />
          {!isCollapsed && (
            <span className="ml-2 text-sm font-medium flex-1 min-w-0">
              {tNav('navigation.errors.failedToLoadWorkspaces')}
            </span>
          )}
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  return (
    <>
      <SidebarMenuItem>
        <DropdownMenu
          open={isOpen}
          onOpenChange={handleOpenChange}
          modal={false}
        >
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              disabled={isWorkspacesLoading}
              size="default"
              variant="outline"
              data-testid="workspace-switcher-trigger"
              className={cn(
                'h-8 !p-1.5 gap-0 group-data-[collapsible=icon]:!p-1.5',
                {
                  'bg-sidebar-accent text-sidebar-accent-foreground':
                    shouldShowSelected,
                  'bg-transparent text-sidebar-foreground': !shouldShowSelected,
                },
              )}
            >
              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                <WorkspaceIdentifier
                  isLoading={isWorkspacesLoading}
                  color={
                    currentWorkspace?.workspace_icon_color as WorkspaceColorType
                  }
                  icon={currentWorkspace?.workspace_icon as WorkspaceIconType}
                />
              </div>
              {!isCollapsed && (
                <>
                  <span className="ml-2 truncate text-sm font-semibold flex-1 min-w-0">
                    {isWorkspacesLoading ? (
                      <Skeleton className="h-5 min-w-32 bg-alternate-1" />
                    ) : (
                      currentWorkspace?.title
                    )}
                  </span>
                  <Icon
                    type="ChevronsUpDown"
                    size="sm"
                    className="text-sidebar-foreground"
                  />
                </>
              )}
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[248px]"
            sideOffset={sidebarState === 'collapsed' ? 16 : 12}
          >
            <DropdownMenuLabel>
              <Text level="small" className="text-muted-foreground">
                {tNav('navigation.workspaceSwitcher.workspaces')}
              </Text>
            </DropdownMenuLabel>

            <div className="max-h-[320px] overflow-y-auto">
              {workspacesWithShortcuts?.length > 0 ? (
                workspacesWithShortcuts.map(workspace => {
                  const workspaceId = parseResourceName(
                    workspace.name!,
                  ).workspace;
                  if (!workspaceId) return null;

                  const handleClick = () => {
                    handleOpenChange(false);
                    handleWorkspaceSelect(workspaceId);
                  };

                  const isActive = workspaceId === currentWorkspaceId;

                  return (
                    <DropdownMenuItem
                      key={workspace.name}
                      onClick={handleClick}
                      className={cn('flex items-center gap-3 px-3 py-2', {
                        'bg-accent/50': isActive,
                      })}
                      data-testid={`workspace-switcher-item-${workspace.title}`}
                    >
                      <WorkspaceIdentifier
                        color={workspace.displayColor as WorkspaceColorType}
                        icon={workspace.displayIcon}
                      />
                      <span className="flex-1 text-sm">{workspace.title}</span>
                      <HotkeyTrigger
                        shortcut={workspace.shortcut!}
                        onHotkeyPress={handleClick}
                      />
                    </DropdownMenuItem>
                  );
                })
              ) : (
                <DropdownMenuItem
                  disabled
                  className="flex items-center gap-3 px-3 py-2"
                >
                  <span className="flex-1 text-sm text-muted-foreground">
                    {tNav('navigation.workspaceSwitcher.noWorkspaces')}
                  </span>
                </DropdownMenuItem>
              )}
            </div>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              onClick={() => {
                handleOpenChange(false);
                openCreateWorkspaceDialog();
              }}
              className="flex items-center gap-3 px-3 py-2"
            >
              <div className="h-5 w-5 rounded-lg bg-muted flex items-center justify-center">
                <Icon type="Plus" size="xs" className="text-muted-foreground" />
              </div>
              <span className="flex-1 text-sm font-medium">
                {tNav('navigation.workspaceSwitcher.createNew')}
              </span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
      <CreateWorkspaceDialog
        isOpen={isCreateWorkspaceDialogOpen}
        onClose={closeCreateWorkspaceDialog}
      />
    </>
  );
};

export default WorkspaceSwitcher;
